/**
 * JS binding for the `WBMStoreKit` native plugin (ipad-native-shell 10.3).
 *
 * StoreKit 2 on iPadOS 17; the Swift side is `frontend/ios/App/App/WBMStoreKit.swift`
 * (shell lane). Lives in `src/platform/` because that is the only place allowed
 * to import `@capacitor/*` (`__tests__/adapterBoundary.test.ts`). Billing code
 * reaches it through `billing/appStore.ts`, never directly.
 *
 * On web, on Android and on any iOS build predating the plugin,
 * `isStoreKitAvailable()` is false and nothing here is called.
 */
import { Capacitor, registerPlugin, type PluginListenerHandle } from "@capacitor/core";

export interface StoreKitProduct {
  id: string;
  displayName: string;
  /** Localized by Apple for the storefront, e.g. "$9.99". Show it verbatim. */
  displayPrice: string;
  /** Present for subscriptions only. */
  periodUnit?: "day" | "week" | "month" | "year";
  periodValue?: number;
  /** A free-trial intro offer's length in days (7 a week, 30 a month, 365 a year). */
  freeTrialDays: number | null;
  eligibleForIntroOffer: boolean;
}

/** `transactionId` is Apple's UInt64, carried as a string. */
export interface StoreKitTransaction {
  jws: string;
  transactionId: string;
}

export type StoreKitPurchaseResult =
  | ({ status: "purchased" } & StoreKitTransaction)
  | { status: "cancelled" }
  /** Ask to Buy or a bank check — it arrives later as `transactionUpdated`. */
  | { status: "pending" };

interface WBMStoreKitPlugin {
  products(opts: { ids: string[] }): Promise<{ products: StoreKitProduct[] }>;
  purchase(opts: { productId: string; appAccountToken: string }): Promise<StoreKitPurchaseResult>;
  finish(opts: { transactionId: string }): Promise<void>;
  currentEntitlements(): Promise<{ transactions: StoreKitTransaction[] }>;
  manageSubscriptions(): Promise<void>;
  addListener(
    event: "transactionUpdated",
    fn: (t: StoreKitTransaction) => void,
  ): Promise<PluginListenerHandle>;
}

const WBMStoreKit = registerPlugin<WBMStoreKitPlugin>("WBMStoreKit");

export function isStoreKitAvailable(): boolean {
  try {
    return Capacitor.isPluginAvailable("WBMStoreKit");
  } catch {
    return false;
  }
}

export const storeKit = {
  products: (ids: string[]) => WBMStoreKit.products({ ids }).then((r) => r.products),
  purchase: (productId: string, appAccountToken: string) =>
    WBMStoreKit.purchase({ productId, appAccountToken }),
  finish: (transactionId: string) => WBMStoreKit.finish({ transactionId }),
  currentEntitlements: () => WBMStoreKit.currentEntitlements().then((r) => r.transactions),
  manageSubscriptions: () => WBMStoreKit.manageSubscriptions(),
  onTransactionUpdated: (fn: (t: StoreKitTransaction) => void) =>
    WBMStoreKit.addListener("transactionUpdated", fn),
};

export type StoreKit = typeof storeKit;
